/**
 * The web need score as a pill: one number, one colour, one word.
 *
 * Drawn in three places that must not disagree - the register table, the map's
 * company card and the company page - so it is a component rather than three
 * spans. The colour comes from `webNeedBand`, so the thresholds live in one
 * place too.
 *
 * A null score is never drawn as a zero. The pass writes every row it reaches,
 * so null means "not computed yet", and a company that has simply not been
 * looked at must not read as a company nobody should call.
 */
import { useT } from "../../../../i18n/I18nProvider";
import { webNeedBand, type WebNeedBand } from "../../../../lib/webNeed";
import { StatusPill, type StatusTone } from "../../../ui";

/**
 * Band to tone. Amber for the ones to call, which is the same colour the map
 * already uses for a company with no website, then blue, then grey. Never red:
 * a high score is good news for the person reading it.
 */
const TONE: Record<WebNeedBand, StatusTone> = {
  high: "wt",
  mid: "info",
  low: "mut",
  none: "mut",
};

export function WebNeedPill({ score, className }: { score: number | null | undefined; className?: string }) {
  const t = useT();
  const band = webNeedBand(score);

  if (score == null) {
    return (
      <StatusPill tone="mut" className={className}>
        {t("ariregister.webNeed.notComputed")}
      </StatusPill>
    );
  }

  return (
    <StatusPill tone={TONE[band]} className={className}>
      <span className="crm-tnum font-semibold">{score}</span>
      <span className="crm-mut ml-1">{t(`ariregister.webNeed.band.${band}`)}</span>
    </StatusPill>
  );
}
